// Slot drawer with two modes:
//   - occupied → item summary + Move / Scrap + movement history
//   - empty/blocked → Block/Unblock toggle (server refuses to block a slot
//     that still holds an item, so the button is always safe to show)

import React, { useState } from 'react';
import { Offcanvas, Badge, Button, Alert } from 'react-bootstrap';

import { useMove, useScrap, usePatchSlot, useItem } from '../../features/warehouse/useItems.js';
import {
  extractErrorMessage,
  movementActionColor,
  movementActionLabel,
  useFlashBanner,
} from '../../features/warehouse/warehouseUtils.js';
import SlotPickerModal from './SlotPickerModal.js';

export default function ItemDetailDrawer({ show, onHide, slotCode, entry, buildingId }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const { banner, flash } = useFlashBanner();

  const move = useMove();
  const scrap = useScrap();
  const patchSlot = usePatchSlot();

  const serial = entry?.item?.serial;
  const itemType = entry?.item?.type;

  const { data: itemDetail } = useItem(show && serial ? serial : null);
  const movements = itemDetail?.movements || [];

  const handleMove = (toSlotCode) => {
    if (!serial) return;
    move.mutate(
      { serial, toSlotCode },
      {
        onSuccess: () => {
          flash('success', `${serial} moved to ${toSlotCode}`);
          onHide();
        },
        onError: (err) =>
          flash('danger', extractErrorMessage(err, 'Move failed'), { autoHide: false }),
      }
    );
  };

  const handleScrap = () => {
    if (!serial) return;
    const reason = window.prompt(`Scrap ${serial}? Enter a reason (optional):`, '');
    if (reason === null) return;
    scrap.mutate(
      { serial, reason },
      {
        onSuccess: () => {
          flash('success', `${serial} scrapped`);
          onHide();
        },
        onError: (err) =>
          flash('danger', extractErrorMessage(err, 'Scrap failed'), { autoHide: false }),
      }
    );
  };

  const handleBlockToggle = () => {
    if (!slotCode) return;
    const nextStatus = entry.status === 'blocked' ? 'empty' : 'blocked';
    patchSlot.mutate(
      { code: slotCode, updates: { status: nextStatus } },
      {
        onSuccess: () => {
          flash('success', `${slotCode} → ${nextStatus}`);
          if (nextStatus === 'blocked') onHide();
        },
        onError: (err) =>
          flash('danger', extractErrorMessage(err, 'Update failed'), { autoHide: false }),
      }
    );
  };

  const busy = move.isPending || scrap.isPending || patchSlot.isPending;

  return (
    <>
      <Offcanvas show={show} onHide={onHide} placement="end">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{slotCode || 'Slot'}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {banner && (
            <Alert variant={banner.variant} className="py-2 small">
              {banner.text}
            </Alert>
          )}

          {!entry && <div className="text-muted">Click a slot to see details.</div>}

          {entry && (
            <>
              <div className="mb-3">
                <Badge
                  bg={
                    entry.status === 'occupied'
                      ? 'success'
                      : entry.status === 'blocked'
                      ? 'danger'
                      : 'secondary'
                  }
                >
                  {entry.status}
                </Badge>
                <span className="text-muted ms-2 small">
                  Aisle {entry.aisle} · Bay {entry.bay} · Level {entry.level}
                </span>
              </div>

              {entry.item ? (
                <div>
                  <h5>{entry.item.serial}</h5>
                  <table className="table table-sm">
                    <tbody>
                      <tr><th>Cable</th><td>{entry.item.cableName}</td></tr>
                      <tr><th>Type</th><td>{entry.item.type}</td></tr>
                      <tr><th>Length</th><td>{entry.item.lengthMeters} m</td></tr>
                      <tr><th>Status</th><td>{entry.item.status}</td></tr>
                      <tr><th>Age</th><td>{entry.item.ageDays} days</td></tr>
                      {entry.item.reservedForOrderId && (
                        <tr>
                          <th>Reserved for</th>
                          <td className="text-primary">{String(entry.item.reservedForOrderId)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <div className="d-grid gap-2 mt-3">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={busy}
                      onClick={() => setPickerOpen(true)}
                    >
                      Move to another slot…
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={busy}
                      onClick={handleScrap}
                    >
                      Scrap
                    </Button>
                  </div>

                  <hr className="my-3" />
                  <div className="small fw-bold text-muted mb-2">History</div>
                  {movements.length === 0 && (
                    <div className="small text-muted fst-italic">No movements yet.</div>
                  )}
                  {movements.length > 0 && (
                    <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                      <table className="table table-sm small mb-0">
                        <tbody>
                          {movements.map(m => (
                            <tr key={m._id}>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                <Badge bg={movementActionColor(m.action)}>
                                  {movementActionLabel(m.action)}
                                </Badge>
                              </td>
                              <td className="text-muted" style={{ fontSize: '0.7rem' }}>
                                {new Date(m.at).toLocaleString()}
                                {m.note && <div className="fst-italic">{m.note}</div>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="text-muted mb-3">
                    Slot is {entry.status}.
                  </div>
                  <div className="d-grid gap-2">
                    <Button
                      variant={entry.status === 'blocked' ? 'outline-success' : 'outline-warning'}
                      size="sm"
                      disabled={busy}
                      onClick={handleBlockToggle}
                    >
                      {entry.status === 'blocked' ? 'Unblock slot' : 'Block slot'}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <SlotPickerModal
        show={pickerOpen}
        onHide={() => setPickerOpen(false)}
        onPick={handleMove}
        buildingId={buildingId}
        title={`Move ${serial || ''} to…`}
        excludeCode={slotCode}
        allowedForType={itemType}
      />
    </>
  );
}
